<template>
  <div class="bpjs-page">
        <PageHeader :icon="config.icon">
      <template #title>
        {{ config.title }}
      </template>
      <template #subtitle>
        {{ config.subtitle }}
      </template>
      <template #actions>
        <button 
                type="button" 
                class="btn btn-primary font-weight-bold px-6 hc-btn-tambah-jadwal mt-4 mt-md-0"
                @click="openForm"
              >
                <i class="fas fa-plus"></i><span>{{ config.addLabel }}</span>
              </button>
      </template>
    </PageHeader>

    <b-card class="bpjs-card bpjs-form-card">
      <b-form class="mb-4" @submit.prevent="load">
        <div class="row align-items-end bpjs-filter-row">
          <div class="col-md-4 bpjs-filter-column">
            <b-form-group label="Tanggal mulai" class="mb-0">
              <b-form-input v-model="filters.startDate" type="date" class="bpjs-form-control" />
            </b-form-group>
          </div>
          <div class="col-md-4 bpjs-filter-column">
            <b-form-group label="Tanggal selesai" class="mb-0">
              <b-form-input v-model="filters.endDate" type="date" class="bpjs-form-control" />
            </b-form-group>
          </div>
          <div class="col-md-4 bpjs-filter-column bpjs-action-column">
            <b-button type="submit" variant="outline-success" block :disabled="loading" class="bpjs-control-button bpjs-outline-action">
              <b-spinner v-if="loading" small class="mr-2" /><i v-else class="fas fa-sync-alt mr-2"></i>Muat Data
            </b-button>
          </div>
        </div>
      </b-form>

      <b-alert :show="Boolean(error)" variant="danger">{{ error }}</b-alert>
      <div class="table-responsive">
        <table class="table bpjs-table">
          <thead>
            <tr>
              <th v-for="column in config.columns" :key="column.key">{{ column.label }}</th>
              <th class="text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td :colspan="config.columns.length + 1" class="text-center py-5"><b-spinner small class="mr-2" />Memuat data...</td></tr>
            <tr v-else-if="!items.length"><td :colspan="config.columns.length + 1" class="bpjs-empty">Belum ada data pada periode ini.</td></tr>
            <template v-else>
              <tr v-for="(item, index) in items" :key="reference(item) || index">
                <td v-for="column in config.columns" :key="column.key">{{ display(item[column.key]) }}</td>
                <td class="text-right">
                  <b-button size="sm" variant="outline-danger" @click="remove(item)">
                    <i class="far fa-trash-alt"></i>
                  </b-button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </b-card>

    <b-modal v-model="formVisible" :title="config.addLabel" size="lg" hide-footer>
      <b-alert show variant="info">Pastikan data sesuai hasil pemeriksaan dan identitas peserta sebelum dikirim ke PCare.</b-alert>
      <b-form @submit.prevent="save">
        <div class="row">
          <div v-for="field in config.fields" :key="field.key" :class="field.wide ? 'col-12' : 'col-md-6'">
            <b-form-group :label="field.label" :label-for="`bpjs-${resource}-${field.key}`">
              <b-form-textarea
                v-if="field.type === 'textarea'"
                :id="`bpjs-${resource}-${field.key}`"
                v-model.trim="form[field.key]"
                rows="3"
                :required="field.required"
              />
              <b-form-input
                v-else
                :id="`bpjs-${resource}-${field.key}`"
                v-model.trim="form[field.key]"
                :type="field.type || 'text'"
                :required="field.required"
                :placeholder="field.placeholder || ''"
              />
            </b-form-group>
          </div>
        </div>
        <div class="d-flex justify-content-end mt-4">
          <b-button variant="light" class="mr-3" @click="formVisible = false">Batal</b-button>
          <b-button type="submit" variant="success" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-2" />Kirim ke BPJS
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { rowsFrom, errorMessage, displayValue, today } from "./bpjs.helpers";

const CONFIG = {
  kunjungan: {
    title: "Kunjungan PCare",
    subtitle: "Pelaporan pelayanan peserta BPJS di klinik.",
    addLabel: "Catat Kunjungan",
    icon: "fas fa-notes-medical",
    reference: "noKunjungan",
    columns: [
      { key: "noKunjungan", label: "No. Kunjungan" },
      { key: "noKartu", label: "No. Kartu" },
      { key: "tglDaftar", label: "Tanggal" },
      { key: "kdPoli", label: "Poli" },
      { key: "keluhan", label: "Keluhan" },
    ],
    fields: [
      { key: "noKartu", label: "Nomor Kartu BPJS", required: true, placeholder: "13 digit nomor peserta" },
      { key: "tglDaftar", label: "Tanggal Daftar", type: "date", required: true },
      { key: "kdPoli", label: "Kode Poli BPJS", required: true },
      { key: "kdDokter", label: "Kode Dokter BPJS" },
      { key: "keluhan", label: "Keluhan", type: "textarea", wide: true },
      { key: "kdDiag1", label: "Diagnosa Utama (ICD-10)" },
      { key: "sistole", label: "Sistole", type: "number" },
      { key: "diastole", label: "Diastole", type: "number" },
      { key: "beratBadan", label: "Berat Badan (kg)", type: "number" },
      { key: "tinggiBadan", label: "Tinggi Badan (cm)", type: "number" },
    ],
  },
  rujukan: {
    title: "Rujukan BPJS",
    subtitle: "Kelola rujukan peserta dari klinik ke FKRTL.",
    addLabel: "Buat Rujukan",
    icon: "fas fa-share-square",
    reference: "noRujukan",
    columns: [
      { key: "noRujukan", label: "No. Rujukan" },
      { key: "noKunjungan", label: "No. Kunjungan" },
      { key: "tglEstRujuk", label: "Tgl. Rujuk" },
      { key: "kdppk", label: "Faskes Tujuan" },
      { key: "kdSubSpesialis", label: "Subspesialis" },
    ],
    fields: [
      { key: "noKunjungan", label: "Nomor Kunjungan PCare", required: true },
      { key: "tglEstRujuk", label: "Tanggal Rencana Rujuk", type: "date", required: true },
      { key: "kdppk", label: "Kode Faskes Tujuan", required: true },
      { key: "kdSubSpesialis", label: "Kode Subspesialis" },
      { key: "kdSarana", label: "Kode Sarana" },
      { key: "kdTacc", label: "Kode TACC" },
      { key: "alasanTacc", label: "Alasan TACC", type: "textarea", wide: true },
    ],
  },
};

export default {
  props: { resource: { type: String, required: true } },
  data: () => ({
    loading: false,
    saving: false,
    formVisible: false,
    items: [],
    form: {},
    filters: { startDate: today(), endDate: today() },
    error: "",
  }),
  computed: {
    config() { return CONFIG[this.resource]; },
  },
  watch: {
    resource: { immediate: true, handler() { this.resetForm(); } },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "BPJS Kesehatan" }, { title: this.config.title }]);
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const query = new URLSearchParams(this.filters).toString();
        const response = await ApiService.get(`bpjs/${this.resource}?${query}`);
        if (response.status >= 400) throw response;
        this.items = rowsFrom(response);
      } catch (error) {
        this.error = errorMessage(error, `Data ${this.resource} belum dapat dimuat.`);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {};
      (this.config ? this.config.fields : []).forEach(field => {
        this.$set(this.form, field.key, field.type === "date" ? today() : "");
      });
    },
    openForm() {
      this.resetForm();
      this.formVisible = true;
    },
    async save() {
      this.saving = true;
      try {
        const payload = Object.keys(this.form).reduce((result, key) => {
          if (this.form[key] !== "") result[key] = this.form[key];
          return result;
        }, {});
        const response = await ApiService.post(`bpjs/${this.resource}`, payload);
        this.$bvToast.toast(response.data.message || "Data berhasil dikirim ke BPJS.", { title: "Berhasil", variant: "success", solid: true });
        this.formVisible = false;
        await this.load();
      } catch (error) {
        this.$bvToast.toast(errorMessage(error), { title: "Gagal", variant: "danger", solid: true });
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      const ref = this.reference(item);
      if (!ref) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(`Hapus ${this.config.title.toLowerCase()} ${ref} dari BPJS?`, {
        title: "Konfirmasi penghapusan",
        okTitle: "Ya, hapus",
        cancelTitle: "Batal",
        okVariant: "danger",
      });
      if (!confirmed) return;
      try {
        await ApiService.delete(`bpjs/${this.resource}/${encodeURIComponent(ref)}`);
        this.$bvToast.toast("Data berhasil dihapus.", { title: "Berhasil", variant: "success", solid: true });
        await this.load();
      } catch (error) {
        this.$bvToast.toast(errorMessage(error), { title: "Gagal", variant: "danger", solid: true });
      }
    },
    reference(item) { return item[this.config.reference] || item[this.config.reference.toLowerCase()]; },
    display: displayValue,
  },
};
</script>
